import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator as CalcIcon, ChevronDown, ShoppingCart, Square as RectIcon, Circle as CircleIcon, Triangle as TriIcon, Shapes as IrregIcon } from 'lucide-react';
import { useCart } from './CartContext';
import { Link } from 'react-router-dom';
import { categories } from '../data/products';
export type Shape = 'rectangle' | 'circle' | 'triangle' | 'irregular';
/**
 * Per-yard price for a calculator material, in dollars.
 *
 * The catalog is the source of truth: matching on name keeps the cart price
 * identical to the product page, so a price change in data/products.ts can
 * never leave the calculator quoting a stale figure.
 *
 * The local `priceRange` string is only a display label ("$65"), so it is
 * parsed as a fallback with its currency symbol stripped — passing it to
 * parseFloat directly yields NaN, which then propagates through every cart
 * total as "$NaN".
 */
function materialPricePerYard(material: {
  name: string;
  priceRange: string;
}): number {
  const catalogMatch = categories.flatMap(c => c.products).find(p => p.name === material.name);
  if (catalogMatch?.pricePerYard) return catalogMatch.pricePerYard;
  const parsed = parseFloat(material.priceRange.replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}
export const projectTypes = [{
  id: 'driveway',
  name: 'Driveway',
  defaultDepth: 4
}, {
  id: 'patio',
  name: 'Patio Base',
  defaultDepth: 4
}, {
  id: 'walkway',
  name: 'Walkway',
  defaultDepth: 3
}, {
  id: 'parking',
  name: 'Parking Pad',
  defaultDepth: 6
}, {
  id: 'french-drain',
  name: 'French Drain',
  defaultDepth: 12
}, {
  id: 'fill',
  name: 'General Fill',
  defaultDepth: 6
}];
export const calcMaterials = [{
  id: '125-minus',
  name: '1.25" Minus',
  density: 1.4,
  priceRange: '$65'
}, {
  id: 'crushed-rock',
  name: '5/8" Minus',
  density: 1.4,
  priceRange: '$65'
}, {
  id: 'walkway-gravel',
  name: 'Walkway Gravel (3/8" Minus)',
  density: 1.4,
  priceRange: '$61'
}, {
  id: 'recycled-asphalt',
  name: 'Recycled Asphalt',
  density: 1.4,
  priceRange: '$38'
}, {
  id: 'recycled-concrete',
  name: 'Recycled Concrete',
  density: 1.4,
  priceRange: '$38'
}, {
  id: 'fill-dirt',
  name: 'Fill Dirt',
  density: 1.3,
  priceRange: '$24'
}, {
  id: 'utility-sand',
  name: 'Utility Sand',
  density: 1.5,
  priceRange: '$52'
}, {
  id: 'gravel-borrow',
  name: 'Gravel Borrow',
  density: 1.4,
  priceRange: '$48'
}, {
  id: '58-clear',
  name: '5/8" Crushed Clear',
  density: 1.35,
  priceRange: '$74'
}, {
  id: '78-drain',
  name: '7/8" Drain Rock',
  density: 1.35,
  priceRange: '$69'
}, {
  id: '125-clear',
  name: '1.25" Crushed Clear',
  density: 1.35,
  priceRange: '$68'
}, {
  id: '15-drain',
  name: '1.5" Drain Rock',
  density: 1.35,
  priceRange: '$69'
}, {
  id: 'pea-gravel',
  name: 'Pea Gravel',
  density: 1.45,
  priceRange: '$72'
}, {
  id: 'topsoil',
  name: '3-Way Topsoil',
  density: 1.1,
  priceRange: '$48'
}, {
  id: '5050-mix',
  name: '50/50 Mix',
  density: 1.1,
  priceRange: '$57'
}, {
  id: 'compost',
  name: 'Compost',
  density: 0.6,
  priceRange: '$66'
}, {
  id: 'fine-bark',
  name: 'Fine Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'medium-bark',
  name: 'Medium Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'coarse-bark',
  name: 'Coarse Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'play-chips',
  name: 'Play Chips',
  density: 0.45,
  priceRange: '$55'
}, {
  id: 'streambed-cobble',
  name: 'Streambed Cobble',
  density: 1.5,
  priceRange: '$61'
}, {
  id: 'river-rock',
  name: 'River Rock',
  density: 1.5,
  priceRange: '$84'
}, {
  id: 'lava-rock',
  name: 'Lava Rock',
  density: 0.75,
  priceRange: '$93'
}, {
  id: 'quarry-rock',
  name: '2-4" Spalls',
  density: 1.5,
  priceRange: '$82'
}, {
  id: '48-quarry',
  name: '4-8" Spalls',
  density: 1.5,
  priceRange: '$85'
}, {
  id: '2-ballast',
  name: '2" Ballast',
  density: 1.5,
  priceRange: '$72'
}, {
  id: 'large-rocks',
  name: 'Large Rocks (1-4 Man)',
  density: 1.6,
  priceRange: '$152'
}, {
  id: 'concrete-sand',
  name: 'Concrete Sand',
  density: 1.35,
  priceRange: '$68'
}, {
  id: 'pit-run',
  name: 'Pit Run',
  density: 1.43,
  priceRange: '$37'
}, {
  id: 'hog-fuel',
  name: 'Hog Fuel',
  density: 0.4,
  priceRange: '$48'
}];
type MaterialCalculatorProps = {
  variant?: 'modal' | 'inline' | 'page';
  initialMaterial?: string;
  initialProjectType?: string;
  /** Opening depth in inches. Overrides the project type's default — used by
   *  the quiz, where the specific answer (a 2–3" top-off vs a 4–6" new base)
   *  implies a depth the project type alone can't express. */
  initialDepth?: number;
  /** One line explaining where the opening depth came from, so the number
   *  doesn't look arbitrary. Clears as soon as the customer edits it. */
  depthNote?: string;
  onAddToCart?: (yards: number, material: string) => void;
};
export function MaterialCalculator({
  variant = 'inline',
  initialMaterial = 'crushed-rock',
  initialProjectType = 'driveway',
  initialDepth,
  depthNote,
  onAddToCart
}: MaterialCalculatorProps) {
  const {
    addToCart,
    openCart
  } = useCart();
  const [projectType, setProjectType] = useState(initialProjectType);
  const [material, setMaterial] = useState(calcMaterials.find(m => m.id === initialMaterial) ? initialMaterial : 'crushed-rock');
  const [shape, setShape] = useState<Shape>('rectangle');
  const [unit, setUnit] = useState<'ft' | 'm'>('ft');
  const [depthUnit, setDepthUnit] = useState<'in' | 'cm'>('in');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [diameter, setDiameter] = useState('');
  const [base, setBase] = useState('');
  const [height, setHeight] = useState('');
  const [depth, setDepth] = useState(() => {
    if (initialDepth && initialDepth > 0) return String(initialDepth);
    const pt = projectTypes.find(p => p.id === initialProjectType);
    return pt ? pt.defaultDepth.toString() : '4';
  });
  // Shown beneath the depth field to explain the opening number. Retired the
  // moment the customer changes the project type or types their own depth —
  // past that point the note would be describing a value that's gone.
  const [activeDepthNote, setActiveDepthNote] = useState(depthNote ?? '');
  const [calcResults, setCalcResults] = useState<{
    yards: number;
    tons: number;
  } | null>(null);
  const handleProjectTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as typeof projectType;
    setProjectType(newType);
    // Depth follows the project type. Previously it didn't, so switching from
    // Driveway to French Drain left 4" in a field that should read 12".
    const pt = projectTypes.find(p => p.id === newType);
    if (pt) {
      setDepth(pt.defaultDepth.toString());
      setActiveDepthNote(`Typical depth for a ${pt.name.toLowerCase()}. Adjust if your project differs.`);
    }
    setCalcResults(null);
  };
  // Clear stale results when shape changes
  useEffect(() => {
    setCalcResults(null);
  }, [shape]);
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    // Convert all linear dimensions to feet (input is ft or m).
    const toFeet = (val: string) => {
      const n = parseFloat(val);
      if (isNaN(n)) return NaN;
      return unit === 'm' ? n * 3.28084 : n;
    };
    // Compute the project area (sq ft) from the active shape's inputs.
    let area = NaN;
    if (shape === 'rectangle' || shape === 'irregular') {
      const l = toFeet(length);
      const w = toFeet(width);
      if (!isNaN(l) && !isNaN(w)) area = l * w;
    } else if (shape === 'circle') {
      const d = toFeet(diameter);
      if (!isNaN(d)) area = Math.PI * (d / 2) ** 2;
    } else if (shape === 'triangle') {
      const b = toFeet(base);
      const h = toFeet(height);
      if (!isNaN(b) && !isNaN(h)) area = 0.5 * b * h;
    }
    // Depth is entered in inches or cm — normalize to feet.
    const depthVal = parseFloat(depth);
    if (isNaN(area) || isNaN(depthVal) || area <= 0 || depthVal <= 0) {
      setCalcResults(null);
      return;
    }
    const depthInches = depthUnit === 'cm' ? depthVal / 2.54 : depthVal;
    const depthFeet = depthInches / 12;
    const cubicFeet = area * depthFeet;
    const cubicYards = cubicFeet / 27;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    const density = selectedMaterial ? selectedMaterial.density : 1.4;
    const tons = cubicYards * density;
    setCalcResults({
      yards: Math.ceil(cubicYards * 10) / 10,
      tons: Math.ceil(tons * 10) / 10
    });
  };
  const handleAddToCartClick = () => {
    if (!calcResults) return;
    const roundedYards = Math.ceil(calcResults.yards);
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    if (!selectedMaterial) return;
    if (onAddToCart) {
      onAddToCart(roundedYards, selectedMaterial.name);
    } else {
      // Dynamically load the splitter utility and add each line item to the cart.
      import('../utils/loadSplit').then(({
        buildDeliveryLineItems
      }) => {
        const lineItems = buildDeliveryLineItems(roundedYards, selectedMaterial.name);
        const pricePerYard = materialPricePerYard(selectedMaterial);
        lineItems.forEach(li => {
          addToCart({
            category: 'Calculated',
            product: li.product,
            quantity: li.quantity,
            pricePerYard
          });
        });
      });
    }
    openCart();
  };
  // CSS class constants based on the variant prop
  const inputClass = 'w-full px-4 py-3 bg-white dark:bg-[#1a1a1a] border border-[#e5e5e5] dark:border-[#333] rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all';
  const containerClass = variant === 'page' ? 'w-full' : variant === 'modal' ? 'w-full' : 'bg-[#f] dark:bg-[#2a2a2a] rounded-2xl border border-[#e5e5e5] dark:border-white/10 shadow-xl overflow-hidden';
  const paddingClass = variant === 'page' ? '' : 'p-6 sm:p-8';
  return <div className={containerClass} data-id="element-1254" data-mp="zb1a64mxb34h">
      <div className={containerClass} data-id="element-1255" data-mp="z9zxxwu68al6">
        <div className={`grid grid-cols-1 ${variant === 'page' ? 'lg:grid-cols-3 gap-8' : 'lg:grid-cols-5'}`} data-id="element-1256" data-mp="uby2sdjh0ktc">
          <div className={`${paddingClass} ${variant === 'page' ? 'lg:col-span-2 space-y-6' : 'lg:col-span-3 border-b lg:border-b-0 lg:border-r border-[#e5e5e5] dark:border-[#555555]'}`} data-id="element-1257" data-mp="6lkmfka90ael">
            {/* Shape Tabs. Icon-led layout — each tab shows the shape
             glyph with a small label below. Reads instantly even in
             narrow embeds (Product page sidebar) where text-only tabs
             would truncate to a single letter. */}
            <div className="dark:bg-[#222] p-1.5 rounded-2xl border border-[#e5e5e5] dark:border-[#333] flex flex-nowrap gap-1 mb-6 bg-[#f2f5f0]" data-id="element-1258" data-mp="3jz5v8u94za0d">
              {[{
              id: 'rectangle' as Shape,
              Icon: RectIcon,
              label: 'Rectangle'
            }, {
              id: 'circle' as Shape,
              Icon: CircleIcon,
              label: 'Circle'
            }, {
              id: 'triangle' as Shape,
              Icon: TriIcon,
              label: 'Triangle'
            }, {
              id: 'irregular' as Shape,
              Icon: IrregIcon,
              label: 'Irregular'
            }].map(({
              id,
              Icon,
              label
            }) => <button key={id} type="button" onClick={() => setShape(id)} aria-label={label} className={`relative flex-1 min-w-0 py-2 px-1 rounded-xl transition-colors z-10 flex flex-col items-center justify-center gap-1 ${shape === id ? 'text-white' : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-200'}`} data-id="element-1259" data-mp="2i7cy6ndyxhb2">
                  {shape === id && <motion.div layoutId="shapeTab" className="absolute inset-0 bg-[#3a3a3a] dark:bg-[#444] rounded-xl -z-10" transition={{
                type: 'spring',
                bounce: 0.2,
                duration: 0.6
              }} data-id="element-1260" data-mp="wx5rxiitee5c" />}
                  <Icon className="w-5 h-5" strokeWidth={shape === id ? 2.25 : 1.75} data-id="element-1261" data-mp="2um1awemuvtia" />
                  <span className="text-[10px] font-semibold leading-none tracking-wide" data-id="element-1262" data-mp="2pqvn1a9w84rk">
                    {label}
                  </span>
                </button>)}
            </div>

            <form onSubmit={handleCalculate} className="space-y-6" data-id="element-1263" data-mp="2gtle3vkjsrpt">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1264" data-mp="2bs5sa1ajqh6d">
                <div data-id="element-1265" data-mp="26tuhnocfg2n3">
                  <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1266" data-mp="26tb1od6c7obp">
                    Project Type
                  </label>
                  <div className="relative" data-id="element-1267" data-mp="5399wejpg0jf">
                    <select value={projectType} onChange={handleProjectTypeChange} className={`${inputClass} appearance-none bg-white pr-14`} data-id="element-1268" data-mp="51y8gldqf4vh">
                      {projectTypes.map(pt => <option key={pt.id} value={pt.id} data-id="element-1269" data-mp="8jlx673gpkzs">
                          {pt.name}
                        </option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-6 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-500 dark:text-stone-400" aria-hidden="true" data-id="element-1270" data-mp="cjw35jr98ben" />
                  </div>
                </div>
                <div data-id="element-1271" data-mp="3lrgkvtg3tx00">
                  <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1272" data-mp="3lryt7og1k1oa">
                    Material
                  </label>
                  <div className="relative" data-id="element-1273" data-mp="3urc2oh460q90">
                    <select value={material} onChange={e => setMaterial(e.target.value)} className={`${inputClass} appearance-none bg-white pr-14`} data-id="element-1274" data-mp="3uqah3ipiwld1">
                      {calcMaterials.map(m => <option key={m.id} value={m.id} data-id="element-1275" data-mp="1a0l1h3gzwvja">
                          {m.name}
                        </option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-6 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-500 dark:text-stone-400" aria-hidden="true" data-id="element-1276" data-mp="1f2k6ehsdw0ic" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end" data-id="element-1277" data-mp="2wym6h1c8rdlg">
                <div className="flex dark:bg-[#1a1a1a] rounded-lg p-1 border border-[#e5e5e5] dark:border-[#333] bg-[#eeeeee]" data-id="element-1278" data-mp="2ww9edvcrrmuo">
                  <button type="button" onClick={() => setUnit('ft')} className={`px-3 py-1 text-xs font-bold rounded-md ${unit === 'ft' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1279" data-mp="36wa2dull2r57">
                    Feet
                  </button>
                  <button type="button" onClick={() => setUnit('m')} className={`px-3 py-1 text-xs font-bold rounded-md ${unit === 'm' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1280" data-mp="3fqoejoathlk3">
                    Meters
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1281" data-mp="84lz1g300f1n">
                <AnimatePresence mode="wait" data-id="element-1282" data-mp="82hucvx9zc0b">
                  {(shape === 'rectangle' || shape === 'irregular') && <motion.div key="rect" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1283" data-mp="d12w4zs6e3qv">
                      <div data-id="element-1284" data-mp="3qbid4mr1vnd8">
                        <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1285" data-mp="3qbs5ggzki6m3">
                          Length ({unit})
                        </label>
                        <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className={`${inputClass} bg-white`} required min="0.1" step="any" data-id="element-1286" data-mp="3ght9jzq37j2l" />
                      </div>
                      <div data-id="element-1287" data-mp="1d2zb103738vw">
                        <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1288" data-mp="1d4anrpembqz5">
                          Width ({unit})
                        </label>
                        <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className={`${inputClass} bg-white`} required min="0.1" step="any" data-id="element-1289" data-mp="13e7mz31998q9" />
                      </div>
                    </motion.div>}
                  {shape === 'circle' && <motion.div key="circ" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} className="col-span-1 sm:col-span-2" data-id="element-1290" data-mp="1r3a4mavw31kr">
                      <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1291" data-mp="apjvuqttrp4s">
                        Diameter ({unit})
                      </label>
                      <input type="number" value={diameter} onChange={e => setDiameter(e.target.value)} placeholder="0" className={inputClass} required min="0.1" step="any" data-id="element-1292" data-mp="2ibtp14t74x1l" />
                    </motion.div>}
                  {shape === 'triangle' && <motion.div key="tri" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1293" data-mp="2r942mwthky9o">
                      <div data-id="element-1294" data-mp="3a2ztfxfy9gwo">
                        <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1295" data-mp="39zlqoo3dj5op">
                          Base ({unit})
                        </label>
                        <input type="number" value={base} onChange={e => setBase(e.target.value)} placeholder="0" className={inputClass} required min="0.1" step="any" data-id="element-1296" data-mp="1fq1n1oxdf4em" />
                      </div>
                      <div data-id="element-1297" data-mp="3mi7qfc1ymtl3">
                        <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1298" data-mp="3iiyxg9equzql">
                          Height ({unit})
                        </label>
                        <input type="number" value={height} onChange={e => setHeight(e.target.value)} placeholder="0" className={inputClass} required min="0.1" step="any" data-id="element-1299" data-mp="3raip6bkys9c2" />
                      </div>
                    </motion.div>}
                </AnimatePresence>

                <div className="col-span-1 sm:col-span-2 border-t border-[#e5e5e5] dark:border-[#333] pt-6 mt-2" data-id="element-1300" data-mp="2zm7e5zz7ckmj">
                  <div className="flex justify-between items-end mb-2" data-id="element-1301" data-mp="38kj5633zdkfi">
                    <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider" data-id="element-1302" data-mp="38mm1f4vq89so">
                      Depth
                    </label>
                    <div className="flex bg-[#eeeeee] dark:bg-[#1a1a1a] rounded-lg p-1 border border-[#e5e5e5] dark:border-[#333]" data-id="element-1303" data-mp="ntsc5cqysw0v">
                      <button type="button" onClick={() => setDepthUnit('in')} className={`px-3 py-1 text-xs font-bold rounded-md ${depthUnit === 'in' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1304" data-mp="25u9sdgrvziwu">
                        Inches
                      </button>
                      <button type="button" onClick={() => setDepthUnit('cm')} className={`px-3 py-1 text-xs font-bold rounded-md ${depthUnit === 'cm' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1305" data-mp="2pfwddsagbben">
                        cm
                      </button>
                    </div>
                  </div>
                  <input type="number" value={depth} onChange={e => {
                  setDepth(e.target.value);
                  setActiveDepthNote('');
                }} className={`${inputClass} bg-white`} placeholder="0" required min="0.1" step="any" data-id="element-1306" data-mp="2knwjagh2bcpz" />
                  {activeDepthNote && depthUnit === 'in' && <p className="mt-2 text-xs leading-relaxed text-stone-600 dark:text-stone-400" data-id="element-1307" data-mp="3tgaqhae4lc21">
                      {activeDepthNote}
                    </p>}
                </div>
              </div>

              <button type="submit" className="w-full py-4 bg-brand-orange hover:bg-brand-orangeHover text-white font-bold rounded-xl transition-colors flex items-center justify-center" data-id="element-1308" data-mp="3km6699thyyvh">
                <CalcIcon className="w-5 h-5 mr-2" data-id="element-1309" data-mp="3anqdl2etnhjv" />
                Calculate Yardage
              </button>
            </form>
          </div>

          <div className={`${paddingClass} ${variant === 'page' ? 'rounded-2xl border border-[#e5e5e5] p-5 sm:p-6 lg:col-span-1 lg:p-8 dark:border-[#333]' : 'lg:col-span-2'} bg-[#ffffff] dark:bg-[#303030] flex flex-col justify-center border-l-4 border-[#CB6E0A] dark:border-[#F6A93B]`} data-id="element-1310" data-mp="3fl8ec5ioqqgs">
            {calcResults ? <div className="space-y-6" data-id="element-1311" data-mp="zum2l7eej0tf">
                <div data-id="element-1312" data-mp="13x0nry5jmcnv">
                  <p className="text-sm font-bold text-[#CB6E0A] dark:text-[#F6A93B] uppercase tracking-wider mb-1" data-id="element-1313" data-mp="13xijmtoamov9">
                    Estimated Volume
                  </p>
                  <div className="flex items-baseline gap-2" data-id="element-1314" data-mp="2kfiw17e69kh1">
                    <span className="font-heading font-bold text-4xl text-stone-900 dark:text-white" data-id="element-1315" data-mp="2y5w2t6fbut4s">
                      {calcResults.yards}
                    </span>
                    <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-1316" data-mp="2tduqxlm2n4fa">
                      Cubic Yards
                    </span>
                  </div>
                </div>
                <div className="pt-4 border-t border-[#e5e5e5] dark:border-[#4b4b4b]" data-id="element-1317" data-mp="334ph312fuo60">
                  <p className="text-sm font-bold text-[#CB6E0A] dark:text-[#F6A93B] uppercase tracking-wider mb-1" data-id="element-1318" data-mp="3h48cque2c71f">
                    Estimated Weight
                  </p>
                  <div className="flex items-baseline gap-2" data-id="element-1319" data-mp="1mpxq8skxxoyt">
                    <span className="font-heading font-bold text-3xl text-stone-900 dark:text-white" data-id="element-1320" data-mp="1hrnry93x688z">
                      {calcResults.tons}
                    </span>
                    <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-1321" data-mp="3hvqbpkwrlb45">
                      Tons
                    </span>
                  </div>
                </div>
                <div className="pt-4 border-t border-[#e5e5e5] dark:border-[#4b4b4b]" data-id="element-1322" data-mp="3rra6u53i85ru">
                  <p className="text-sm font-bold text-[#CB6E0A] dark:text-[#F6A93B] uppercase tracking-wider mb-1" data-id="element-1323" data-mp="3vupx8jozd9w3">
                    Material Price Range
                  </p>
                  <p className="text-lg font-medium text-stone-900 dark:text-white" data-id="element-1324" data-mp="9lx1m3ys4680">
                    {(() => {
                  // Read the displayed price from the catalog for the same
                  // reason the cart does — a price change in
                  // data/products.ts must never leave this label stale.
                  const m = calcMaterials.find(m => m.id === material);
                  return m ? `${materialPricePerYard(m)}` : '—';
                })()}{' '}
                    <span className="text-sm text-stone-500 font-normal" data-id="element-1325" data-mp="2cuqnvp2n9flv">
                      / yard
                    </span>
                  </p>
                </div>

                {variant === 'page' ? <div className="mt-6 space-y-3" data-id="element-1326" data-mp="23u0wqyobwdeh">
                    {/* The standalone calculator page used to dead-end at
                        "Shop Materials", making the customer re-find the
                        material and re-enter the quantity they just worked
                        out. It now finishes the job: the rounded yardage is
                        split into truckloads and dropped straight in the
                        cart, where the address sets the delivered total. */}
                    <button type="button" onClick={handleAddToCartClick} className="w-full py-4 rounded-xl font-bold transition-colors flex items-center justify-center bg-brand-orange hover:bg-[#e66a2c] text-white shadow-lg shadow-brand-orange/20" data-id="element-1327" data-mp="2hulgpy5zcf9k">
                      <ShoppingCart className="w-5 h-5 mr-2" data-id="element-1328" data-mp="3ahfygnbex0jc" />
                      Add {Math.ceil(calcResults.yards)} Yards to Cart
                    </button>
                    <p className="text-xs leading-relaxed text-stone-500 dark:text-stone-400" data-id="element-1329" data-mp="2z2l0qjqvnu2w">
                      Rounded up to {Math.ceil(calcResults.yards)} yards — the
                      amount we can actually load. Delivery is billed per
                      truckload and added once you enter your address in the
                      cart. 3-yard minimum.
                    </p>
                    <Link to="/#materials" className="block w-full py-3 rounded-xl border border-[#e5e5e5] dark:border-[#4b4b4b] text-center text-sm font-bold text-stone-700 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-white/5 transition-colors" data-id="element-1330" data-mp="3ht2glnqm2v6u">
                      Browse all materials
                    </Link>
                  </div> : <button type="button" onClick={handleAddToCartClick} className="w-full mt-6 py-3.5 bg-brand-charcoal dark:bg-white text-white dark:text-stone-900 font-bold rounded-xl hover:bg-stone-800 dark:hover:bg-stone-100 transition-colors flex items-center justify-center" data-id="element-1331" data-mp="j4f363tv2i6">
                    <ShoppingCart className="w-4 h-4 mr-2" data-id="element-1332" data-mp="ankuxzhb4jlz" />
                    {onAddToCart ? `Apply ${Math.ceil(calcResults.yards)} Yards` : `Add ${Math.ceil(calcResults.yards)} Yards to Cart`}
                  </button>}
              </div> : <div className="text-center text-stone-500 dark:text-stone-400" data-id="element-1333" data-mp="jhpowrrqmb5q">
                <CalcIcon className="w-12 h-12 mx-auto mb-4 opacity-20 text-[#CB6E0A] dark:text-[#F6A93B]" data-id="element-1334" data-mp="3rxiw2g06iaa4" />
                <p data-id="element-1335" data-mp="3nwqm1kjhg1cs">Enter your dimensions to see estimated volume and weight.</p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}